// Canva-style visual browsing gallery for the bottom EFFECTS section. Three
// independently collapsible categories — Effect, Filter, Shadow — each a grid of
// live preview cards (the element's own image / a gradient with the candidate
// applied), with dynamic controls under the active preset. Reuses the
// propertyEffects engine; writes element.effect / filterPreset / shadow.
//
// onCommit = history snapshot (card click / color change). onLive = live slider.

import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  EFFECT_CATALOG, FILTER_CATALOG, SHADOW_CATALOG,
  getEffectDefaults, getFilterDefaults, getShadowDefaults,
  getEffectFields, composeFilter,
} from './propertyEffects';

const COLLAPSED_COUNT = 3;
const COLOR_KEYS = new Set(['color', 'highlight', 'shadow', 'strokeColor']);
const RANGES = {
  opacity: [0, 1, 0.01], blur: [0, 60, 1], distance: [0, 100, 1], angle: [0, 360, 1],
  offsetX: [-50, 50, 1], offsetY: [-50, 50, 1], size: [1, 4, 1], thickness: [0, 20, 1],
  softness: [0, 60, 1], depth: [0, 20, 1], intensity: [0, 2, 0.05], amount: [1, 10, 1],
  levels: [2, 8, 1], spread: [0, 40, 1], glowSize: [0.5, 3, 0.1], strokeWidth: [0, 20, 1],
};
const cap = (s) => s.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());

const ParamControl = ({ name, value, onChange, onColorChange }) => {
  if (COLOR_KEYS.has(name)) {
    return (
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] text-gray-500">{cap(name)}</span>
        <input type="color" value={(value || '#000000').startsWith('#') ? value : '#000000'}
          onChange={(e) => onColorChange(e.target.value)} className="w-8 h-6 rounded border cursor-pointer p-0.5" />
      </div>
    );
  }
  const [min, max, step] = RANGES[name] || [0, 100, 1];
  return (
    <div className="space-y-0.5">
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-gray-500">{cap(name)}</span>
        <span className="text-[10px] text-gray-400 tabular-nums">{Math.round((value ?? 0) * 100) / 100}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value ?? 0}
        onChange={(e) => onChange(Number(e.target.value))} className="w-full accent-violet-600" />
    </div>
  );
};

// Live preview swatch: the element's image (or a gradient) with just this
// candidate applied, so the user sees the effect on their content.
const PreviewSwatch = ({ field, item, previewSrc }) => {
  const filter = item.id === 'none' ? 'none'
    : composeFilter({ id: 'preview', [field]: { type: item.id, params: (item.defaults || {}) } });
  return (
    <div className="w-full aspect-square bg-white flex items-center justify-center overflow-hidden">
      <div
        className="w-[72%] h-[72%] rounded-sm bg-gradient-to-br from-violet-300 via-fuchsia-300 to-amber-200"
        style={{
          backgroundImage: previewSrc ? `url(${previewSrc})` : undefined,
          backgroundSize: 'cover', backgroundPosition: 'center',
          filter,
        }}
      />
    </div>
  );
};

const Category = ({ title, field, catalog, scope, getDefaults, element, previewSrc, onCommit, onLive }) => {
  const [expanded, setExpanded] = useState(false);
  const fields = getEffectFields(element);
  const active = fields[field] || {};
  const activeType = active.type || 'none';
  const params = { ...getDefaults(activeType), ...(active.params || {}) };

  const items = catalog.filter((c) => !c.scope || c.scope === 'both' || c.scope === scope);
  const shown = expanded ? items : items.slice(0, COLLAPSED_COUNT);
  const controls = catalog.find((c) => c.id === activeType)?.controls || [];

  const select = (id) => onCommit(field, id === 'none' ? null : { type: id, params: getDefaults(id) });
  const setParam = (key, v, live = true) => (live ? onLive : onCommit)(field, { type: activeType, params: { ...params, [key]: v } });

  return (
    <div className="pb-1">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        data-testid={`gallery-toggle-${field}`}
        className="w-full flex items-center justify-between py-1.5"
      >
        <Label>{title}</Label>
        {expanded ? <ChevronUp className="w-3.5 h-3.5 text-gray-400" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-400" />}
      </button>
      <div
        className="grid grid-cols-3 gap-1.5 overflow-hidden transition-all duration-300 ease-in-out"
        style={{ maxHeight: expanded ? 2000 : 230 }}
      >
        {shown.map((item) => {
          const isActive = activeType === item.id;
          return (
            <button
              key={item.id}
              type="button"
              data-testid={`gallery-${field}-${item.id}`}
              onClick={() => select(item.id)}
              aria-pressed={isActive}
              title={item.label}
              className={`group relative rounded-md overflow-hidden border transition-all ${isActive ? 'border-purple-500 ring-2 ring-purple-200' : 'border-gray-200 hover:border-purple-300'}`}
            >
              <PreviewSwatch field={field} item={item} previewSrc={previewSrc} />
              <span className="block text-[9px] text-center py-0.5 bg-white text-gray-600 truncate px-1">{item.label}</span>
            </button>
          );
        })}
      </div>
      {activeType !== 'none' && controls.length > 0 && (
        <div className="pt-2 mt-2 space-y-2 border-t border-gray-100" data-testid={`gallery-controls-${field}`}>
          {controls.map((key) => (
            <ParamControl
              key={key}
              name={key}
              value={params[key]}
              onChange={(v) => setParam(key, v, true)}
              onColorChange={(v) => setParam(key, v, false)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const Label = ({ children }) => (
  <span className="text-[10px] text-gray-500 uppercase tracking-wide font-medium">{children}</span>
);

const EffectGallery = ({ element, onCommit, onLive }) => {
  if (!element) return null;
  const commit = onCommit || onLive || (() => {});
  const live = onLive || commit;
  const scope = element.type === 'text' ? 'text' : 'visual';
  const previewSrc = element.type === 'image' ? element.src : (element.image?.src || null);

  return (
    <div data-testid="effect-gallery" className="space-y-1">
      <Category title="Effect" field="effect" catalog={EFFECT_CATALOG} scope={scope}
        getDefaults={getEffectDefaults} element={element} previewSrc={previewSrc} onCommit={commit} onLive={live} />
      {scope !== 'text' && (
        <Category title="Filter" field="filterPreset" catalog={FILTER_CATALOG} scope={scope}
          getDefaults={getFilterDefaults} element={element} previewSrc={previewSrc} onCommit={commit} onLive={live} />
      )}
      <Category title="Shadow" field="shadow" catalog={SHADOW_CATALOG} scope={scope}
        getDefaults={getShadowDefaults} element={element} previewSrc={previewSrc} onCommit={commit} onLive={live} />
    </div>
  );
};

export default EffectGallery;
